import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import './App.css';
import './styles/devicePreview.css';
import './styles/deviceContainer.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { GlobalUIProvider } from './context/GlobalUIContext';
import MainLayout from './layouts/MainLayout';
import { VoiceProvider } from './context/VoiceContext';
import { PWAProvider } from './context/PWAContext';
import { WelcomeProvider } from './context/WelcomeContext';
import { OnboardingProvider } from './context/OnboardingContext';
import OnboardingTour from './components/OnboardingTour';
import { NotificationsProvider } from './context/NotificationsContext';
import { UserLimitsProvider } from './state/userLimits';
import useMediaQuery from './hooks/useMediaQuery';
import Dashboard from './components/Dashboard';
import ChatPanel from './components/ChatPanel';
import Header from './components/Header';
import AuthPage from './components/AuthPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import NotFound from './components/NotFound';
import WelcomeScreen from './components/WelcomeScreen';
import LogoutConfirmModal from './components/LogoutConfirmModal';
// 🟥 CUADRO NEGRO SUPREMO 2026-02-26 — ConsentGate ELIMINADO de toda la app.
import LegalGate from './components/LegalGate';
import RestoreUndoBanner from './components/RestoreUndoBanner';
import GroupsProjects from './components/GroupsProjects';
import ServicesInfo from './components/ServicesInfo';
import StepResolutionPanel from './components/StepResolutionPanel';
import InternalWorkspace from './components/InternalWorkspace';
import AutoVisualCreationModal from './components/AutoVisualCreationModal';
// 🟥 P4.3 2026-07-06 — SEO/LCP: lazy loading agresivo de páginas ocasionales.
// Cada import() genera un chunk propio que se descarga sólo cuando el usuario
// visita esa ruta. Reduce el main bundle inicial ~35-45%.
const HomePage = lazy(() => import('./components/Home'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const SharedConversation = lazy(() => import('./components/SharedConversation'));
const SharedRecipePage = lazy(() => import('./pages/SharedRecipePage'));
const SubscriptionReturnPage = lazy(() => import('./pages/SubscriptionReturnPage'));
const SubscripcionCompletadaPage = lazy(() => import('./pages/SubscripcionCompletadaPage'));
const DesignSystemShowcase = lazy(() => import('./components/DesignSystemShowcase'));
const AppViewer = lazy(() => import('./components/AppViewer'));
// 🟥 P26.40 · Sitios publicados por subdominio propio.
const SubdomainSitePage = lazy(() => import('./pages/SubdomainSitePage'));
const isSiteSubdomain = () => {
  const domain = process.env.REACT_APP_PUBLIC_SITE_DOMAIN || 'damyxia.site';
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  return host.endsWith(`.${domain}`) && host.split('.').length > domain.split('.').length;
};
const CommunityGallery = lazy(() => import('./components/CommunityGallery').then((m) => ({ default: m.CommunityGallery })));
const SharedDesignView = lazy(() => import('./components/CommunityGallery').then((m) => ({ default: m.SharedDesignView })));
import EmailVerificationBanner from './components/EmailVerificationBanner';
import { saveImageToGallery } from './components/ImageGallery';
import SubscriptionUpgradeOverlayMount from './components/SubscriptionUpgradeOverlayMount';
import TermsReacceptModal from './components/TermsReacceptModal';
import SplashScreen from './components/SplashScreen';
import WelcomeFlow from './components/WelcomeFlow';
import PlanesYBeneficios from './components/PlanesYBeneficios';
const HistoriaDamyxia = lazy(() => import('./pages/dashboard/HistoriaDamyxia'));
import HomeModals from './components/HomeModals';
import FuncionPremiumModal from './components/FuncionPremiumModal';
import IOSInstallBanner from './components/IOSInstallBanner';
import IOSInstallConfirmToast from './components/IOSInstallConfirmToast';
import { getToken, setToken as setMemToken, clearToken } from './utils/authToken';
const SubscriptionSuccess = lazy(() => import('./pages/subscription/success'));
const SubscriptionCancel = lazy(() => import('./pages/subscription/cancel'));
const ReferidosPage = lazy(() => import('./pages/ReferidosPage'));
const SistemaDeRecompensas = lazy(() => import('./components/SistemaDeRecompensas'));
const ReferralsAdmin = lazy(() => import('./pages/admin/ReferralsAdmin'));
const AuditAnalyticsPage = lazy(() => import('./pages/admin/AuditAnalyticsPage'));
const PublicAuditReportPage = lazy(() => import('./pages/public/PublicAuditReportPage'));
const PublicModulePage = lazy(() => import('./pages/public/PublicModulePage'));
const CreatorDashboard = lazy(() => import('./pages/admin/CreatorDashboard'));
const BuilderPage = lazy(() => import('./pages/admin/BuilderPage'));
const BackupsAdminPage = lazy(() => import('./pages/admin/BackupsAdminPage'));
const LyonValoracionesPage = lazy(() => import('./pages/admin/LyonValoracionesPage'));
const CreatorModerationPage = lazy(() => import('./pages/admin/CreatorModerationPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminLogs = lazy(() => import('./pages/admin/AdminLogs'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminImages = lazy(() => import('./pages/admin/AdminImages'));
const AdminMCSU = lazy(() => import('./pages/admin/AdminMCSU'));
const IntentMetricsAdminPage = lazy(() => import('./pages/admin/IntentMetricsAdminPage'));
const MCSUWorkspaceView = lazy(() => import('./pages/admin/MCSUWorkspaceView'));
const AdminSecurityEvents = lazy(() => import('./pages/admin/AdminSecurityEvents'));
const MyAppsPage = lazy(() => import('./pages/MyAppsPage'));
const BuilderProjectsPage = lazy(() => import('./pages/BuilderProjectsPage'));
const MisTrabajosPage = lazy(() => import('./pages/MisTrabajosPage'));
// 🟥 CUADRO NEGRO MAESTRO (sec 2) — Widget "Continuar donde dejaste" ELIMINADO
// (abría el Builder por tarjeta).
const UiBuilderPage = lazy(() => import('./pages/UiBuilderPage'));
import PwaUpdateBanner from './components/PwaUpdateBanner';
// 🟥 SPRINT 2 — ErrorBoundary con reporte a Sentry para componentes críticos.
import SentryErrorBoundary from './components/system/SentryErrorBoundary';
// 🟦 Toast in-app real-time para notificaciones de moderación (submission_*).
import NotificationToastListener from './components/system/NotificationToastListener';
// 🟦 SPRINT 3 — Marketplace público (sin auth).
// 🟥 P4.2 2026-07-04 — Lazy-loading para sacar el marketplace del bundle inicial.
// Los usuarios que no visitan /marketplace ni /marketplace/r/:id nunca cargan
// estos componentes, reduciendo el peso del entry chunk.
const PublicMarketplacePage = lazy(() =>
  import('./pages/PublicMarketplace').then((m) => ({ default: m.PublicMarketplacePage })),
);
const PublicRecipePage = lazy(() =>
  import('./pages/PublicMarketplace').then((m) => ({ default: m.PublicRecipePage })),
);
const RemixesHallOfFamePage = lazy(() =>
  import('./pages/PublicMarketplace').then((m) => ({ default: m.RemixesHallOfFamePage })),
);
// Lazy: AdminPanel solo lo cargan los 3 admins. Sacarlo del bundle inicial
// libera ~peso del componente para todos los usuarios Free + Premium que
// nunca lo abriran. Se envuelve en <Suspense fallback={null}> en la Route.
const AdminPanel = lazy(() => import('./admin/AdminPanel'));
// 🟥 P4.3 2026-07-06 — Lazy resto de admin/páginas ocasionales.
const AdminEmail = lazy(() => import('./pages/admin/AdminEmail'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const FeedbackAdmin = lazy(() => import('./pages/admin/FeedbackAdmin'));
const AutoFixLoopDashboard = lazy(() => import('./pages/AutoFixLoopDashboard'));
const Capabilities = lazy(() => import('./pages/Capabilities'));
const UserUsage = lazy(() => import('./pages/dashboard/usage'));
const DevPortal = lazy(() => import('./pages/DevPortal'));
const ReferralsDashboard = lazy(() => import('./pages/ReferralsDashboard'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const UserSubmissionsPage = lazy(() => import('./pages/UserSubmissionsPage'));
const MyComponentsPage = lazy(() => import('./pages/MyComponentsPage'));
const Terminos = lazy(() => import('./pages/Terminos'));
const Privacidad = lazy(() => import('./pages/Privacidad'));

// Componente helper: deep-link `/workspace/internal` que marca un flag en
// sessionStorage para que el Dashboard, al montarse despues del Navigate,
// active forceInternalWorkspace incluso cuando el listener de evento global
// aun no esta registrado.
function OpenInternalWorkspaceRedirect() {
  useEffect(() => {
    try {
      sessionStorage.setItem('damyxia_open_internal_workspace', '1');
      window.dispatchEvent(new Event('damyxia-open-internal-module'));
    } catch (_) { /* noop */ }
  }, []);
  return <Navigate to="/" replace />;
}

// ── Email Verification Gatekeeper (frontend gate) ────────────────────────
// Si el usuario esta autenticado pero `is_verified=false` y no esta en la
// whitelist de owners, lo forzamos a `/verify-email`. Las unicas rutas que
// se permiten antes de verificar son las de auth y la propia pantalla de
// verificacion. El backend tambien bloquea con 403 EMAIL_NOT_VERIFIED via
// middleware, esto solo mejora UX (evita peticiones que iban a fallar).
const ADMIN_WHITELIST = new Set([
  'damyxia@gmail.com',
  'marcmarl2@gmail.com',
  'manager@damyxia.com',
]);

const PRE_VERIFY_ALLOWED_PREFIXES = [
  '/verify-email',
  '/verify',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/terminos',
  '/privacidad',
  // CUADRO NEGRO — Alias en inglés (mismo componente que las rutas ES).
  '/terms',
  '/privacy',
];

function VerificationGate({ isVerified, userEmail }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (isVerified) return;
    const emailLower = (userEmail || '').toLowerCase();
    if (emailLower && ADMIN_WHITELIST.has(emailLower)) return;
    const path = location.pathname;
    const allowed = PRE_VERIFY_ALLOWED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`)) || path.startsWith('/verify');
    if (allowed) return;
    navigate('/verify-email', { replace: true });
  }, [isVerified, userEmail, location.pathname, navigate]);
  return null;
}

// Alias para links de referido viejos: `/login?ref=XXXX&lang=es` debe redirigir
// a `/` preservando los query params para que el flujo de captura del codigo
// de referido funcione correctamente desde la home.
function LoginRedirect() {
  const search = typeof window !== 'undefined' ? window.location.search : '';
  return <Navigate to={`/${search}`} replace />;
}
const Precios = lazy(() => import('./pages/Precios'));
const HistorialAvanzado = lazy(() => import('./components/HistorialAvanzado'));
const PipelinesPage = lazy(() => import('./pages/dashboard/pipelines'));
const AutomationsPage = lazy(() => import('./pages/dashboard/automations'));
const WorkspacePage = lazy(() => import('./pages/dashboard/workspace'));
const WorkspaceTemplateEditorPage = lazy(() => import('./pages/dashboard/workspace-template-editor'));
const PublicDashboard = lazy(() => import('./pages/PublicDashboard'));
// P26.31 · Visor de enlaces temporales de vista previa.
const TemporaryPreviewPage = lazy(() => import('./pages/TemporaryPreviewPage'));
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
// 🟥 Carga del helper showSuccess para que window.damyxiaShowSuccess
// esté disponible en cualquier ruta (incluida la home pública). El
// import sólo dispara el side-effect de registrar el debug global; no
// inflamos el bundle de la home porque el módulo pesa < 1KB.
import './utils/showSuccess';
import { API_URL } from './config/api';
import { cacheConversationList, getCachedConversationList, cacheConversation, getCachedConversation, isOffline, onConnectivityChange } from './lib/offlineCache';
// CUADRO NEGRO — División Pragmática App.js: 3 hooks en /hooks/app/
import useAppState from './hooks/app/useAppState';
import useImagePublish from './hooks/app/useImagePublish';
import useChatSend from './hooks/app/useChatSend';
// CUADRO NEGRO — Footer legal global (compatible con .App overflow:hidden)
import Footer from './components/layout/Footer';
// 🟦 CUADRO NEGRO — Footer condicional por ruta (oculto en Builder/MyApps/etc).
import RouteAwareFooter from './components/layout/RouteAwareFooter';

// API endpoint
const API = API_URL;

const Home = ({ token, onLogout, onLoginClick, onRequestLogout, isVerified, onVerified }) => {
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAppOffline, setIsAppOffline] = useState(!navigator.onLine);
  
  // Hook para detectar si es desktop (md: 768px+)
  const isDesktop = useMediaQuery('(min-width: 768px)');
  
  // Persistir el ID de la conversación actual en localStorage
  const CONVERSATION_STORAGE_KEY = 'damyxia_current_conversation_id';
  const [showGroupsProjects, setShowGroupsProjects] = useState(false);
  const [showServicesInfo, setShowServicesInfo] = useState(false);
  const [showStepPanel, setShowStepPanel] = useState(false);
  const [openConversationsModal, setOpenConversationsModal] = useState(false);
  const [showVerificationBanner, setShowVerificationBanner] = useState(false);

  // CUADRO NEGRO — idioma elevado al inicio del componente: lo necesitan
  // los hooks useChatSend y otros handlers más abajo. ANTES estaba al
  // final del componente (línea ~595), lo cual rompía TDZ al usarlo en
  // las llamadas a hooks de arriba.
  const { language } = useLanguage();

  useEffect(() => {
    setShowVerificationBanner(!isVerified && token && token !== 'guest');
  }, [isVerified, token]);
  
  // CUADRO NEGRO — Estados de UI extraídos a useAppState
  const {
    mobileView,
    setMobileView,
    swipeHandlers,
    chatPanelExpanded,
    setChatPanelExpanded,
    workspaceExpanded,
    setWorkspaceExpanded,
    forceInternalWorkspace,
    setForceInternalWorkspace,
    showImageEditor,
    setShowImageEditor,
    activeModuleId,
  } = useAppState();

  // 🟥 CUADRO NEGRO 2026-06-30 (Opción A — árbol estable) — ref imperativo
  // al Panel del Chat para colapsarlo/expandirlo cuando el usuario apreta
  // la flechita "Expandir Workspace" sin desmontar Dashboard.
  const chatPanelRef = useRef(null);
  // Flag para aplicar transición CSS suave SOLO durante el toggle
  // programático (no durante el drag manual del PanelResizeHandle).
  const [chatPanelAnimating, setChatPanelAnimating] = useState(false);
  useEffect(() => {
    const panel = chatPanelRef.current;
    if (!panel) return;
    setChatPanelAnimating(true);
    const timeout = setTimeout(() => setChatPanelAnimating(false), 360);
    if (workspaceExpanded) {
      try { panel.collapse(); } catch (e) { void e; }
    } else {
      try { panel.expand(); } catch (e) { void e; }
    }
    return () => clearTimeout(timeout);
  }, [workspaceExpanded]);

  // CUADRO NEGRO — Imagen publicada al Workspace extraída a useImagePublish
  const {
    latestWorkspaceImage,
    setLatestWorkspaceImage,
  } = useImagePublish();

  // 🟥 CUADRO NEGRO 2026-07-02 — Auto-Visual Creation (Fase 2 FULL).
  // Escucha el evento del modal AVC y fuerza abrir el InternalWorkspace.
  // El payload con {verb_id, code, source} ya fue guardado en sessionStorage
  // por AutoVisualCreationModal y será consumido por InternalWorkspace.
  useEffect(() => {
    const onAvcOpenWorkspace = () => {
      try {
        sessionStorage.setItem('damyxia_open_internal_workspace', '1');
      } catch (_) { /* noop */ }
      setForceInternalWorkspace(true);
      window.dispatchEvent(new Event('damyxia-open-internal-module'));
    };
    window.addEventListener('damyxia:open-internal-workspace', onAvcOpenWorkspace);
    return () => window.removeEventListener('damyxia:open-internal-workspace', onAvcOpenWorkspace);
  }, [setForceInternalWorkspace]);

  // New states for IdeaLightbulb actions
  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const [showDiagramGenerator, setShowDiagramGenerator] = useState(false);
  const [showInstallApp, setShowInstallApp] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const fileInputRef = useRef(null);
  
  // 🟥 Estado del proyecto activo — para el botón GitHub del header (LED verde parpadeando).
  // Lo actualiza el Dashboard via CustomEvent('damyxia:active-project').
  const [activeProjectFiles, setActiveProjectFiles] = useState([]);
  useEffect(() => {
    const handler = (e) => {
      const files = (e && e.detail && Array.isArray(e.detail.files)) ? e.detail.files : [];
      setActiveProjectFiles(files);
    };
    window.addEventListener('damyxia:active-project', handler);
    return () => window.removeEventListener('damyxia:active-project', handler);
  }, []);

  // State for current communication profile (detected or manual)
  const [currentProfile, setCurrentProfile] = useState(null);
  const [styleWasManual, setStyleWasManual] = useState(false);
  const [levelWasManual, setLevelWasManual] = useState(false);
  
  // State for active modules indicator
  const [activeModules, setActiveModules] = useState([]);

  // FASE 11.2: Sync post-restore-snapshot.
  // Cuando se restaura un snapshot global, el `currentConversation.id` que
  // tenemos en memoria puede quedar stale (apunta a una conversación que el
  // usuario quiso "deshacer"). Limpiamos el state y recargamos la lista para
  // evitar el GET stale → 404 → toast falso "Conversación no encontrada".
  // También avisamos al Workspace para que refresque su módulo abierto.
  useEffect(() => {
    const handler = () => {
      // Marcar ventana de gracia: 3s en los que loadConversation 404 NO
      // muestra toast (evita falso positivo causado por el state drift).
      window.__damyxia_restore_grace_until = Date.now() + 3000;
      setCurrentConversation(null);
      try { loadConversations && loadConversations(); } catch (_) { /* noop */ }
      // Si hay un módulo activo en Workspace, pedir refresh.
      if (activeModuleId) {
        try {
          window.dispatchEvent(new CustomEvent('damyxia:internal-module-updated', {
            detail: { moduleId: activeModuleId, action: 'restored' },
          }));
        } catch (_) { /* noop */ }
      }
    };
    window.addEventListener('damyxia:snapshot-restored', handler);
    return () => window.removeEventListener('damyxia:snapshot-restored', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModuleId]);

  
  // State for chart data from AI responses
  const [currentChartData, setCurrentChartData] = useState(null);

  // Handler para guardar imagen en galería
  const handleSaveImageToGallery = (imageData) => {
    if (saveImageToGallery(imageData)) {
      toast.success(language === 'es' ? 'Imagen guardada en la galería' : 'Image saved to gallery');
    } else {
      toast.error(language === 'es' ? 'Error al guardar imagen' : 'Error saving image');
    }
  };
  
  // Check verification status on mount
  useEffect(() => {
    const checkVerification = async () => {
      if (token && token !== 'guest') {
        try {
          const response = await axios.get(`${API}/auth/me`, {
          });
          if (response.data.is_verified) {
            onVerified();
            setShowVerificationBanner(false);
          } else {
            setShowVerificationBanner(true);
          }
        } catch (error) {
          // Ignore errors
        }
      }
    };
    checkVerification();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Remove any Emergent branding elements after React mounts
  useEffect(() => {
    const removeEmergentElements = () => {
      const selectors = [
        '#emergent-badge',
        '[id*="emergent"]',
        'a[href*="emergent.sh"]',
        'a[href*="emergentagent"]',
        'img[src*="emergent"]',
        '[class*="emergent"]'
      ];
      
      selectors.forEach(selector => {
        try {
          document.querySelectorAll(selector).forEach(el => {
            if (!el.closest('#root')) {
              el.remove();
            }
          });
        } catch (e) {}
      });
    };
    
    // Run after mount and on interval to catch late injections
    removeEmergentElements();
    const interval = setInterval(removeEmergentElements, 2000);
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    loadConversations();
    // Listen for connectivity changes
    const cleanup = onConnectivityChange((online) => {
      setIsAppOffline(!online);
      if (online) {
        toast.success(language === 'es' ? 'Conexión restaurada' : 'Connection restored');
        loadConversations();
      } else {
        toast.info(language === 'es' ? 'Sin conexión - modo offline' : 'No connection - offline mode');
      }
    });
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Restaurar la conversación guardada al cargar
  // DESHABILITADO: El usuario quiere que siempre inicie limpio
  useEffect(() => {
    // Limpiar cualquier conversación guardada al iniciar sesión
    // Así el área de trabajo y el chat siempre empiezan limpios
    localStorage.removeItem(CONVERSATION_STORAGE_KEY);
    setCurrentConversation(null);
  }, [token]); // Solo se ejecuta cuando cambia el token (login/logout)

  // 🟥 CUADRO NEGRO 2026-07-05 — Exponer el conversation_id activo en window
  // para que otros componentes (InternalWorkspace, workspaces adjuntos)
  // puedan adjuntarlo automáticamente al hacer POST/PUT/DELETE a los
  // endpoints de módulos y proyectos. Habilita la reconstrucción por chat.
  useEffect(() => {
    try {
      window.__damyxia_active_conversation_id = currentConversation?.id || null;
    } catch (_) { /* noop */ }
  }, [currentConversation?.id]);

  // Ya no guardamos el ID de conversación - siempre inicia limpio
  // El usuario puede seleccionar conversaciones desde el historial si lo desea

  const loadConversations = async () => {
    try {
      if (isOffline()) {
        // Offline: use cached data
        const cached = getCachedConversationList();
        if (cached.length > 0) {
          setConversations(cached);
        }
        return;
      }
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      const response = await axios.get(`${API}/conversations`, { headers });
      setConversations(response.data);
      // Cache for offline use
      cacheConversationList(response.data);
    } catch (error) {
      if (error.response?.status === 401 && token !== 'guest') {
        onLogout();
      }
      // Fallback to cache on network error
      if (!error.response) {
        const cached = getCachedConversationList();
        if (cached.length > 0) {
          setConversations(cached);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createNewConversation = async (initialMessage = null) => {
    try {
      // Si initialMessage es un evento de click, ignorarlo
      const message = (typeof initialMessage === 'string') ? initialMessage : null;
      
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      const response = await axios.post(
        `${API}/conversations`,
        { title: message ? message.substring(0, 50) : 'Nueva conversación' },
        { headers }
      );
      const newConv = response.data;
      setConversations(prev => [newConv, ...prev]);
      setCurrentConversation(newConv);
      
      // Si hay mensaje inicial, enviarlo automáticamente
      if (message) {
        sendMessage(message);
      }
      
      return newConv;
    } catch (error) {
      if (error.response?.status === 401 && token !== 'guest') {
        onLogout();
      }
      toast.error(language === 'es' ? 'Error al crear conversación' : 'Error creating conversation');
      return null;
    }
  };

  const deleteConversation = async (conversationId, opts = {}) => {
    // Spec oficial Papelera: confirmar antes de mover a la Papelera (30 dias).
    // skipConfirm=true: el caller ya mostro un modal React de confirmacion (necesario en iframes
    // donde window.confirm puede ser bloqueado por las politicas del navegador).
    if (!opts.skipConfirm) {
      const confirmMsg = language === 'es'
        ? 'Esta conversacion se enviara a la Papelera y podras recuperarla durante 30 dias. Los usuarios Free deben pagar para recuperarla. ¿Continuar?'
        : 'This conversation will be sent to Trash and can be recovered within 30 days. Free users must pay to recover it. Continue?';
      if (!window.confirm(confirmMsg)) return;
    }
    try {
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      const res = await axios.delete(`${API}/conversations/${conversationId}`, { headers });
      // Use functional update to ensure we're working with latest state
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        localStorage.removeItem(CONVERSATION_STORAGE_KEY);
      }
      const alreadyGone = res?.data?.already_gone;
      toast.success(
        alreadyGone
          ? (language === 'es' ? 'Conversación fantasma eliminada de la lista' : 'Ghost conversation removed from list')
          : (language === 'es' ? 'Conversacion enviada a la Papelera' : 'Conversation sent to Trash')
      );
      window.dispatchEvent(new CustomEvent('damyxia:trash-updated'));
    } catch (error) {
      throw error; // Re-throw to allow caller to handle
    }
  };

  // Cerrar conversación actual sin eliminarla
  const closeCurrentConversation = () => {
    setCurrentConversation(null);
    localStorage.removeItem(CONVERSATION_STORAGE_KEY);
  };

  const selectConversation = async (conversationId) => {
    try {
      if (isOffline()) {
        // Offline: use cached conversation
        const cached = getCachedConversation(conversationId);
        if (cached) {
          setCurrentConversation(cached);
        } else {
          toast.info(language === 'es' ? 'Conversación no disponible offline' : 'Conversation not available offline');
        }
        return;
      }
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      const response = await axios.get(`${API}/conversations/${conversationId}`, { headers });
      setCurrentConversation(response.data);
      // Cache for offline use
      cacheConversation(response.data);

      // 🟥 CUADRO NEGRO 2026-07-05 — Reconstrucción automática de proyecto.
      // Al abrir una conversación desde el Historial, si tiene módulos creados
      // via execution_log, abrir automáticamente el Área Interna de Trabajo
      // con el último módulo seleccionado. Safe-fail: si falla, no bloquea.
      try {
        const rec = await axios.get(`${API}/execution/${conversationId}/reconstruct`);
        const lastModuleId = rec.data?.last_module_id;
        if (lastModuleId) {
          try { sessionStorage.setItem('damyxia_open_internal_workspace', '1'); } catch (_) { /* noop */ }
          setForceInternalWorkspace(true);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', { detail: { moduleId: lastModuleId } }));
          }, 120);
        }
      } catch (_) { /* safe-fail */ }
    } catch (error) {
      if (error.response?.status === 404) {
        // Fase 11.2: si estamos en la ventana de gracia post-restore-snapshot,
        // el 404 es esperado (el ID quedó stale). Limpiamos sin toast falso.
        const graceUntil = (typeof window !== 'undefined' && window.__damyxia_restore_grace_until) || 0;
        if (Date.now() <= graceUntil) {
          setCurrentConversation(null);
        } else {
          toast.error(language === 'es' ? 'Conversación no encontrada' : 'Conversation not found');
          setCurrentConversation(null);
        }
      } else if (error.response?.status === 401) {
        toast.error(language === 'es' ? 'Sesión expirada' : 'Session expired');
      } else if (!error.response) {
        // Network error - try cache
        const cached = getCachedConversation(conversationId);
        if (cached) {
          setCurrentConversation(cached);
          toast.info(language === 'es' ? 'Mostrando versión guardada' : 'Showing cached version');
        } else {
          toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
        }
      } else {
        toast.error(language === 'es' ? 'Error al cargar conversación' : 'Error loading conversation');
      }
    }
  };

  // CUADRO NEGRO — sendMessage extraído a useChatSend (recibe setters
  // de UI como dependencias, comportamiento idéntico).
  const { sendMessage } = useChatSend({
    token,
    language,
    currentConversation,
    setCurrentConversation,
    createNewConversation,
    selectConversation,
    loadConversations,
    activeModuleId,
    setCurrentProfile,
    setStyleWasManual,
    setLevelWasManual,
    setActiveModules,
    setCurrentChartData,
    setForceInternalWorkspace,
  });

  const filteredConversations = conversations.filter((conv, index) => {
    const convNumber = (index + 1).toString();
    const query = searchQuery.toLowerCase().trim();
    
    // Search by number or title
    return convNumber === query || 
           convNumber.startsWith(query) ||
           conv.title.toLowerCase().includes(query);
  });

  // Handle file attachment from IdeaLightbulb
  const handleAttachFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');

    if (!isImage) {
      toast.error(language === 'es'
        ? `Solo se admiten imágenes. "${file.name}" no es una imagen.`
        : `Only images are supported. "${file.name}" is not an image.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      toast.loading(language === 'es' ? 'Subiendo imagen...' : 'Uploading image...', { id: 'upload' });

      // Leer como base64 (data URL completa)
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('read_failed'));
        reader.readAsDataURL(file);
      });

      const base64Content = typeof dataUrl === 'string' && dataUrl.includes(',')
        ? dataUrl.split(',')[1]
        : dataUrl;

      const authHeaders = {};  // P2.1 cookie-only via axios.defaults

      // 1) Persistir la imagen en conversation_images (vision/process)
      const ingestRes = await axios.post(
        `${API}/vision/process`,
        {
          image_base64: base64Content,
          instruction: '',
          conversation_id: currentConversation?.id || null,
        },
        { headers: authHeaders }
      );

      const imageId = ingestRes.data?.image_id;
      if (!imageId) throw new Error('image_id missing from vision/process');

      // 2) PUBLICAR la imagen en el Área de Trabajo (workspace_items)
      //    Sin este paso la imagen queda como "pendiente" e invisible para
      //    el Dashboard, que solo lista items publicados.
      const publishRes = await axios.post(
        `${API}/workspace/publish`,
        {
          image_id: imageId,
          display_name: file.name.replace(/\.[^.]+$/, ''),
        },
        { headers: authHeaders }
      );

      toast.success(
        language === 'es'
          ? `Imagen "${file.name}" disponible en el Área de Trabajo`
          : `Image "${file.name}" available in the Workspace`,
        { id: 'upload', duration: 4000 }
      );

      // Notifica al Dashboard para que recargue la lista de items publicados
      window.dispatchEvent(new CustomEvent('workspace-image-published', {
        detail: {
          imageId,
          workspaceItem: publishRes.data?.workspace_item,
        },
      }));

      // Si estamos en una conversación activa, refrescarla para ver el mensaje
      if (currentConversation?.id) {
        await selectConversation(currentConversation.id);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(
        language === 'es'
          ? `Error al subir imagen: ${detail}`
          : `Error uploading image: ${detail}`,
        { id: 'upload' }
      );
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="relative h-[100dvh] overflow-hidden bg-zinc-950 light:bg-zinc-50 transition-colors duration-300 pb-0 sm:pb-7 app-root-mobile-safe">
      {/* 🟪 P25.9 · pb-0 en mobile (era pb-7=28px) porque el .legal-footer
          ya está oculto en ≤640px vía CSS @media. Los 28px reservados
          creaban un gap innecesario entre el composer del chat y la
          BottomNav (Chat/Trabajo/Papelera/Historial). En desktop se
          mantiene pb-7 para el footer legal global. */}
      {/* pb-7 (28px) reserva espacio para el footer legal global fijo en
          el fondo del viewport (.legal-footer height: 28px). Evita que el
          textarea del chat o la bottom-toolbar móvil queden tapados.
          🟥 FIX MOBILE 2026-02-25 — La clase `.app-root-mobile-safe` añade
          padding-top responsivo en móvil para que el header del Dashboard
          (Chat y Workspace) NO quede pegado debajo de la barra de URL del
          browser. Ver index.css. */}
      {/* Hidden file input for attachment */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        className="hidden"
        accept=".txt,.js,.jsx,.ts,.tsx,.py,.html,.css,.json,.md,.yaml,.yml,.xml,.sql,.sh,.java,.cpp,.c,.go,.rs,.php,.rb,.swift,.kt,.csv,.pdf,.docx,.png,.jpg,.jpeg,.webp,.gif"
      />
      
      <Header 
        onLogout={token ? onRequestLogout : null} 
        onLoginClick={onLoginClick} 
        isLoggedIn={!!token}
        onOpenConversations={() => setOpenConversationsModal(true)}
        activeProjectFiles={activeProjectFiles}
      />

      {/* 🟥 CUADRO NEGRO MAESTRO (sec 2) — El widget "Continuar donde dejaste"
          abría el Builder por tarjeta: ELIMINADO. El Builder visual sólo se
          abre desde /builder-projects con el botón manual aislado. */}
      
      {/* Offline Indicator */}
      {isAppOffline && (
        <div className="fixed top-14 left-0 right-0 z-50 bg-amber-600/90 backdrop-blur-sm text-white text-center py-1.5 text-xs font-medium" data-testid="offline-banner">
          {language === 'es' ? 'Sin conexión — mostrando datos guardados' : 'Offline — showing cached data'}
        </div>
      )}
      
      {/* Email Verification Banner — DESACTIVADO 2026-05-08 a pedido del dueño:
         no debe aparecer un banner amarillo grande en el dashboard. La
         verificación sigue funcionando (la página /verify-email sigue activa
         y los flujos críticos como publicar app/comentar siguen exigiendo
         verificación), simplemente no se renderiza el aviso flotante.
         Si en algun momento se quiere reactivar, basta volver a poner el
         JSX del bloque dentro de este `false &&`. */}
      {false && showVerificationBanner && token !== 'guest' && (
        <div className="fixed top-12 sm:top-16 left-0 right-0 z-40 px-4 py-2">
          <EmailVerificationBanner 
            onVerified={() => {
              onVerified();
              setShowVerificationBanner(false);
            }} 
          />
        </div>
      )}
      
      {/* Navegación Móvil — 4 accesos directos: Chat / Trabajo / Papelera / Historial.
         Bottom-nav fijo con backdrop. Cada boton tiene min-h ~56px (>44px hit
         target Apple HIG) y label legible. */}
      {!isDesktop && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-900/95 backdrop-blur-lg border-t border-zinc-800">
          <div className="flex">
            <button
              onClick={() => setMobileView('chat')}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 px-1 transition-all ${
                mobileView === 'chat'
                  ? 'text-indigo-400 bg-indigo-500/10'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid="mobile-nav-chat"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-[10px] font-medium">{language === 'es' ? 'Chat' : 'Chat'}</span>
            </button>

            <button
              onClick={() => {
                // CUADRO NEGRO mobile: tap en "Trabajo" SOLO cambia la vista.
                // NO abre automáticamente InternalWorkspace — el usuario debe
                // abrirlo manualmente. Si estaba abierto previamente en esta
                // sesión, lo cerramos para mostrar el Empty state.
                setMobileView('workspace');
                setForceInternalWorkspace(false);
              }}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 px-1 transition-all ${
                mobileView === 'workspace'
                  ? 'text-emerald-400 bg-emerald-500/10'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid="mobile-nav-workspace"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-[10px] font-medium">{language === 'es' ? 'Trabajo' : 'Work'}</span>
            </button>

            <button
              onClick={() => { window.location.assign('/historial?tab=trash'); }}
              className="flex-1 flex flex-col items-center gap-0.5 py-2.5 px-1 transition-all text-zinc-500 hover:text-amber-300"
              data-testid="mobile-nav-trash"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-[10px] font-medium">{language === 'es' ? 'Papelera' : 'Trash'}</span>
            </button>

            <button
              onClick={() => { window.location.assign('/historial'); }}
              className="flex-1 flex flex-col items-center gap-0.5 py-2.5 px-1 transition-all text-zinc-500 hover:text-cyan-300"
              data-testid="mobile-nav-history"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-[10px] font-medium">{language === 'es' ? 'Historial' : 'History'}</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Contenido principal - Una sola instancia de ChatPanel */}
      {isDesktop ? (
        // DESKTOP: PanelGroup con resize
        <div className="h-full">
          {chatPanelExpanded ? (
            /* Modo expandido - Chat ocupa toda la pantalla */
            <div className="h-full pt-[88px]">
              <ChatPanel
                conversations={filteredConversations}
                currentConversation={currentConversation}
                onSelectConversation={selectConversation}
                onDeleteConversation={deleteConversation}
                onCloseConversation={closeCurrentConversation}
                onSendMessage={sendMessage}
                onRefreshConversation={selectConversation}
                onNewConversation={createNewConversation}
                onUpdateMessages={(newMessages) => {
                  if (currentConversation) {
                    setCurrentConversation({
                      ...currentConversation,
                      messages: newMessages
                    });
                  }
                }}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onOpenGroupsProjects={() => setShowGroupsProjects(true)}
                onOpenServicesInfo={() => setShowServicesInfo(true)}
                onOpenStepPanel={() => setShowStepPanel(true)}
                onAttachFile={handleAttachFile}
                onOpenImageGenerator={() => setShowImageGenerator(true)}
                onOpenDiagramGenerator={() => setShowDiagramGenerator(true)}
                onInstallApp={() => setShowInstallApp(true)}
                onOpenImageGallery={() => setShowImageGallery(true)}
                onOpenConversationsModal={() => setOpenConversationsModal(true)}
                currentProfile={currentProfile}
                styleWasManual={styleWasManual}
                levelWasManual={levelWasManual}
                activeModules={activeModules}
                activeModuleId={activeModuleId}
                onGoToWorkspace={null}
                onExpandPanel={() => setChatPanelExpanded(!chatPanelExpanded)}
                isExpanded={chatPanelExpanded}
              />
            </div>
          ) : (
            /* 🟥 CUADRO NEGRO 2026-06-30 (Opción A — árbol estable) —
               Refactor para EVITAR que Dashboard se desmonte al expandir el
               Workspace. Antes: 2 ramas del ternario renderizaban Dashboard
               en árboles distintos → React lo destruía + recreaba, perdiendo
               los ~30 useState locales de ImageEditor / WorkspaceImageViewer
               (zoom, rotation, crop, filtros, historial, mascaras, etc.).
               Ahora: PanelGroup SIEMPRE, con el Panel del Chat colapsable
               (collapsedSize=0). El toggle expandir/colapsar el chat via
               ref imperativo — Dashboard permanece en la misma posición
               del árbol → React lo reconcilia, no lo desmonta.
               Se preservan TODOS los estados locales de sub-componentes
               (imágenes cargadas manualmente, textos, ediciones, iframe
               de la Vista Previa React, historial, etc.). */
            <PanelGroup direction="horizontal" className="h-full pt-[88px]">
              {/* Left Panel - Chat (colapsable a 0% cuando workspaceExpanded) */}
              <Panel
                ref={chatPanelRef}
                defaultSize={45}
                minSize={30}
                maxSize={60}
                collapsible={true}
                collapsedSize={0}
                style={chatPanelAnimating ? { transition: 'flex 320ms cubic-bezier(0.4, 0, 0.2, 1)' } : undefined}
              >
                <div data-panel-id="chat-panel" className="h-full">
                  <ChatPanel
                    conversations={filteredConversations}
                    currentConversation={currentConversation}
                    onSelectConversation={selectConversation}
                    onDeleteConversation={deleteConversation}
                    onCloseConversation={closeCurrentConversation}
                    onSendMessage={sendMessage}
                    onRefreshConversation={selectConversation}
                    onNewConversation={createNewConversation}
                    onUpdateMessages={(newMessages) => {
                      if (currentConversation) {
                        setCurrentConversation({
                          ...currentConversation,
                          messages: newMessages
                        });
                      }
                    }}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    onOpenGroupsProjects={() => setShowGroupsProjects(true)}
                    onOpenServicesInfo={() => setShowServicesInfo(true)}
                    onOpenStepPanel={() => setShowStepPanel(true)}
                    onAttachFile={handleAttachFile}
                    onOpenImageGenerator={() => setShowImageGenerator(true)}
                    onOpenDiagramGenerator={() => setShowDiagramGenerator(true)}
                    onInstallApp={() => setShowInstallApp(true)}
                    onOpenImageGallery={() => setShowImageGallery(true)}
                    onOpenConversationsModal={() => setOpenConversationsModal(true)}
                    currentProfile={currentProfile}
                    styleWasManual={styleWasManual}
                    levelWasManual={levelWasManual}
                    activeModules={activeModules}
                    activeModuleId={activeModuleId}
                    onGoToWorkspace={null}
                    onExpandPanel={() => setChatPanelExpanded(!chatPanelExpanded)}
                    isExpanded={chatPanelExpanded}
                  />
                </div>
              </Panel>

              {/* Handle oculto cuando el chat está colapsado (workspaceExpanded=true) */}
              <PanelResizeHandle
                className={`w-1 bg-zinc-800/50 hover:bg-indigo-500 active:bg-indigo-600 cursor-col-resize relative group transition-all duration-300 ${workspaceExpanded ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
              >
                <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-1 h-12 bg-indigo-500 rounded-full shadow-lg shadow-indigo-500/50" />
                </div>
              </PanelResizeHandle>

              {/* Right Panel - Dashboard (SIEMPRE en la misma posición del árbol) */}
              <Panel minSize={40}>
                <div data-panel-id="dashboard-panel" className="h-full">
                  <Dashboard
                    conversations={conversations}
                    onSelectConversation={selectConversation}
                    currentConversation={currentConversation}
                    chartData={currentChartData}
                    forceInternalWorkspace={forceInternalWorkspace}
                    onInternalWorkspaceClosed={() => setForceInternalWorkspace(false)}
                    isWorkspaceExpanded={workspaceExpanded}
                    onExpandWorkspace={() => setWorkspaceExpanded(!workspaceExpanded)}
                    latestWorkspaceImage={latestWorkspaceImage}
                    setLatestWorkspaceImage={setLatestWorkspaceImage}
                    showImageEditor={showImageEditor}
                    setShowImageEditor={setShowImageEditor}
                  />
                </div>
              </Panel>
            </PanelGroup>
          )}
        </div>
      ) : (
        // MOBILE: Vista condicional Chat/Workspace con soporte de gestos swipe
        // 🟦 2026-06-27 (rev3) — padding-top responsivo: 120px base + safe-area
        // (era 76px → +44px adicionales). Pedido del creador: bajar MÁS la
        // mini-barra (MindPanelHeader y WorkspaceTopBar) para que queden
        // 🟪 P25.8 · OWNER MANDATE (feb-2026) — Reducir espacio negro
        // entre el Header global (~48px + safe-area) y la barra de
        // "DamyxIA · Home · Bombilla". Antes eran 120px reservados, lo
        // que dejaba ~72px de aire vacío. Bajado a 60px → el chat y el
        // workspace suben ~60px y ganan altura visible real.
        // Sin notch → 60px. Con notch → 60px + inset.
        <div 
          {...swipeHandlers}
          className="h-full relative overflow-hidden pt-[calc(env(safe-area-inset-top,0px)+88px)] pb-14 touch-pan-y"
        >
          {/* Solo renderiza el panel activo - NO ambos */}
          {mobileView === 'chat' ? (
            <div className="h-full w-full">
              <ChatPanel
                conversations={filteredConversations}
                currentConversation={currentConversation}
                onSelectConversation={selectConversation}
                onDeleteConversation={deleteConversation}
                onCloseConversation={closeCurrentConversation}
                onSendMessage={sendMessage}
                onRefreshConversation={selectConversation}
                onNewConversation={createNewConversation}
                onUpdateMessages={(newMessages) => {
                  if (currentConversation) {
                    setCurrentConversation({
                      ...currentConversation,
                      messages: newMessages
                    });
                  }
                }}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onOpenGroupsProjects={() => setShowGroupsProjects(true)}
                onOpenServicesInfo={() => setShowServicesInfo(true)}
                onOpenStepPanel={() => setShowStepPanel(true)}
                onAttachFile={handleAttachFile}
                onOpenImageGenerator={() => setShowImageGenerator(true)}
                onOpenDiagramGenerator={() => setShowDiagramGenerator(true)}
                onInstallApp={() => setShowInstallApp(true)}
                onOpenImageGallery={() => setShowImageGallery(true)}
                onOpenConversationsModal={() => setOpenConversationsModal(true)}
                currentProfile={currentProfile}
                styleWasManual={styleWasManual}
                levelWasManual={levelWasManual}
                activeModules={activeModules}
                activeModuleId={activeModuleId}
                onGoToWorkspace={() => setMobileView('workspace')}
                onExpandPanel={null}
                isExpanded={false}
              />
            </div>
          ) : (
            <div className="h-full w-full">
              <Dashboard 
                conversations={conversations}
                onSelectConversation={selectConversation}
                currentConversation={currentConversation}
                chartData={currentChartData}
                forceInternalWorkspace={forceInternalWorkspace}
                onInternalWorkspaceClosed={() => setForceInternalWorkspace(false)}
                isWorkspaceExpanded={workspaceExpanded}
                onExpandWorkspace={() => setWorkspaceExpanded(!workspaceExpanded)}
                latestWorkspaceImage={latestWorkspaceImage}
                setLatestWorkspaceImage={setLatestWorkspaceImage}
                showImageEditor={showImageEditor}
                setShowImageEditor={setShowImageEditor}
              />
            </div>
          )}
        </div>
      )}
      
      <HomeModals
        token={token}
        showGroupsProjects={showGroupsProjects} setShowGroupsProjects={setShowGroupsProjects}
        showServicesInfo={showServicesInfo} setShowServicesInfo={setShowServicesInfo}
        showStepPanel={showStepPanel} setShowStepPanel={setShowStepPanel}
        openConversationsModal={openConversationsModal} setOpenConversationsModal={setOpenConversationsModal}
        showImageGenerator={showImageGenerator} setShowImageGenerator={setShowImageGenerator}
        showImageGallery={showImageGallery} setShowImageGallery={setShowImageGallery}
        showDiagramGenerator={showDiagramGenerator} setShowDiagramGenerator={setShowDiagramGenerator}
        showInstallApp={showInstallApp} setShowInstallApp={setShowInstallApp}
        conversations={conversations}
        currentConversation={currentConversation}
        selectConversation={selectConversation}
        deleteConversation={deleteConversation}
        handleSaveImageToGallery={handleSaveImageToGallery}
      />
    </div>
  );
};

function App() {
  const [token, setToken] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [termsUpdateRequired, setTermsUpdateRequired] = useState(false);
  const [currentTermsVersion, setCurrentTermsVersion] = useState(1);
  const [userEmail, setUserEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(true);

  // Track si la app corre como PWA instalada (standalone). Una vez por sesion.
  useEffect(() => {
    try {
      const isStandalone =
        window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      if (isStandalone && !sessionStorage.getItem('damyxia-pwa-installed-tracked')) {
        sessionStorage.setItem('damyxia-pwa-installed-tracked', '1');
        import('./utils/trackPwa').then(({ trackPwa }) =>
          trackPwa('pwa_installed', { standalone: true })
        );
      }
    } catch {}
  }, []);

  // Al cargar la app, verificar si hay sesión activa via cookie httpOnly
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch(`${API_URL}/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setToken('cookie-session');
          setMemToken('cookie-session');
          setIsVerified(data.is_verified || false);
          setUserEmail(data.email || '');
          localStorage.setItem('userEmail', data.email || '');
          localStorage.setItem('isPremium', data.is_premium ? 'true' : 'false');
          if (data.avatar) {
            try { localStorage.setItem('userPhoto', data.avatar); } catch {}
          }
          // 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 — Flag global de sesión lista.
          // Permite a componentes anidados (MarketplacePanel) esperar
          // EXPLÍCITAMENTE a que /auth/me haya respondido OK antes de
          // disparar sus propios fetchs. Elimina la race condition donde
          // el modal abría y llamaba a /api/apps antes de que el browser
          // tuviera el cookie sincronizado.
          try {
            window.__damyxia_session_ready = true;
            window.dispatchEvent(new CustomEvent('damyxia:session-ready'));
          } catch (_) {}
        } else {
          // 401/403/etc → sesión NO válida.
          try {
            window.__damyxia_session_ready = false;
            window.dispatchEvent(new CustomEvent('damyxia:session-not-ready'));
          } catch (_) {}
        }
      } catch (e) {
        // No hay sesión activa
        try {
          window.__damyxia_session_ready = false;
        } catch (_) {}
      }
      setAuthLoading(false);
    };
    
    // Si hay skipAuth, mantener compatibilidad
    const skipAuth = localStorage.getItem('skipAuth');
    if (skipAuth) {
      setToken(skipAuth);
      setMemToken(skipAuth);
      setAuthLoading(false);
      try { window.__damyxia_session_ready = true; } catch (_) {}
    } else {
      // 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 — Diferir checkSession hasta
      // después de la hidratación (rAF doble = garantiza al menos un paint).
      // Antes corría sincrónamente en el primer useEffect, lo que en Prod
      // competía con el SW activate y a veces el cookie aún no estaba
      // disponible en el primer request.
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => { checkSession(); });
        });
      } else {
        checkSession();
      }
    }
  }, []);

  const handleLoginSuccess = (newToken, verified = true, termsRequired = false, termsVersion = 1) => {
    setToken(newToken);
    setMemToken(newToken);
    setIsVerified(verified);
    setTermsUpdateRequired(termsRequired);
    setCurrentTermsVersion(termsVersion);
    // Email is already saved to localStorage by AuthPage
    const savedEmail = localStorage.getItem('userEmail') || '';
    setUserEmail(savedEmail);
    // 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 (FIX iter 310) — Tras login
    // exitoso, marcar la sesión como lista INMEDIATAMENTE. El backend ya
    // setteó la cookie httpOnly damyxia_token en la respuesta de /auth/login,
    // así que el siguiente fetch a cualquier endpoint protegido es seguro.
    // SIN esto, el flag quedaba en `false` (de la check pre-login fallida)
    // y waitForSessionReady() en el Marketplace devolvía false sin esperar,
    // disparando el race condition que justamente queríamos evitar.
    try {
      window.__damyxia_session_ready = true;
      window.dispatchEvent(new CustomEvent('damyxia:session-ready'));
    } catch (_) {}
    // Aislamiento estricto: forzar refresh del UserLimitsProvider con el
    // nuevo token (descarta cualquier estado del usuario anterior).
    try { window.dispatchEvent(new CustomEvent('damyxia:auth-changed')); } catch (_) {}
  };

  const handleTermsAccepted = () => {
    setTermsUpdateRequired(false);
  };

  const handleVerified = () => {
    setIsVerified(true);
  };

  const handleRequestLogout = () => {
    setShowLogoutModal(true);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, { method: 'POST' });
    } catch (e) { /* ignora */ }
    clearToken();
    // Limpieza completa de datos privados del usuario
    localStorage.removeItem('skipAuth');
    localStorage.removeItem('userPhoto');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('isPremium');
    localStorage.removeItem('userName');
    localStorage.removeItem('userBio');
    localStorage.removeItem('userId');
    localStorage.removeItem('damyxia_style');
    localStorage.removeItem('damyxia_level');
    localStorage.removeItem('damyxia_saved_images');
    localStorage.removeItem('damyxia_hide_subscription');
    localStorage.removeItem('damyxia_image_viewer_closed');
    localStorage.removeItem('damyxia-user-liked');
    localStorage.removeItem('damyxia-saved-components');
    localStorage.removeItem('damyxia-onboarding');
    localStorage.removeItem('damyxia-welcome');
    localStorage.removeItem('conversationsBtnPosition');
    localStorage.removeItem('damyxia_current_conversation_id');
    setToken(null);
    setIsVerified(false);
    setTermsUpdateRequired(false);
    setShowLogoutModal(false);
    setUserEmail('');
    // 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 — Tras logout, marcar la sesión
    // como NO lista (la cookie httpOnly ya fue invalidada por el backend).
    try { window.__damyxia_session_ready = false; } catch (_) {}
    // Aislamiento estricto: notifica a UserLimitsProvider para que descarte
    // cualquier estado del usuario anterior antes de re-fetchar.
    try { window.dispatchEvent(new CustomEvent('damyxia:auth-changed')); } catch (_) {}
  };

  const handleSaveAndLogout = () => {
    toast.success('Trabajo guardado correctamente');
    handleLogout();
  };

  const handleLogoutWithoutSave = () => {
    handleLogout();
  };

  // Si cargando sesión, mostrar pantalla en blanco breve
  // 🟥 CUADRO NEGRO ÚNICO Y REAL 2026-02-26 — Gate `if (authLoading)` ELIMINADO.
  // Antes, mientras /api/auth/me hacía su round-trip (2–3s en conexiones
  // lentas / cold start del backend) se renderizaba un div oscuro fullscreen
  // que el usuario percibía como "pantalla negra inicial". Ahora la UI se
  // monta INMEDIATAMENTE: si `token === null` se muestra la WelcomeScreen
  // (route público), y cuando checkSession resuelve, React re-renderiza
  // automáticamente al tree autenticado. Sin gate, sin pantalla negra.

  // Si no hay token, mostrar pantalla de bienvenida
  if (!token) {
    return (
      <LanguageProvider>
        <GlobalUIProvider>
        <WorkspaceProvider>
        <VoiceProvider>
        <PWAProvider>
          <WelcomeProvider>
            <OnboardingProvider>
              <div className="App">
                <SplashScreen />
                <BrowserRouter>
                  {/* 🟥 P4.3 2026-07-06 — Suspense global para lazy routes. */}
                  <Suspense fallback={null}>
                  <MainLayout>
                  <Routes>
                    <Route path="/" element={<WelcomeScreen />} />
                    <Route path="/home" element={<HomePage />} />
                    <Route 
                      path="/login" 
                      element={<AuthPage onLoginSuccess={handleLoginSuccess} />}
                    />
                    <Route path="/verify" element={<VerifyEmailPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ForgotPassword />} />
                    <Route path="/shared/:shareCode" element={<SharedConversation />} />
                    <Route path="/share/recipe" element={<SharedRecipePage />} />
                    {/* 🟦 SPRINT 3 — Marketplace público (acceso sin auth) */}
                    {/* 🟥 P4.2 — Envuelto en Suspense por lazy-loading del chunk marketplace. */}
                    <Route path="/marketplace" element={<Suspense fallback={null}><PublicMarketplacePage /></Suspense>} />
                    <Route path="/marketplace/remixes" element={<Suspense fallback={null}><RemixesHallOfFamePage /></Suspense>} />
                    <Route path="/marketplace/r/:id" element={<Suspense fallback={null}><PublicRecipePage /></Suspense>} />
                    <Route path="/design-system" element={<DesignSystemShowcase />} />
                    <Route path="/design-system/gallery" element={<CommunityGallery />} />
                    <Route path="/design-system/shared/:shareCode" element={<SharedDesignView />} />
                    <Route path="/app/:slug" element={<AppViewer />} />
                    <Route path="/planes-y-beneficios" element={<PlanesYBeneficios />} />
                    <Route path="/planes" element={<PlanesYBeneficios />} />
                    <Route path="/builder" element={<Navigate to="/home" replace />} />
                    <Route path="/sistema-de-recompensas" element={<SistemaDeRecompensas />} />
                    <Route path="/admin/referrals" element={<ReferralsAdmin />} />
                    <Route path="/admin/audit-analytics" element={<AuditAnalyticsPage />} />
                    <Route path="/public/audit-report/:token" element={<PublicAuditReportPage />} />
                    <Route path="/public/module/:slug" element={<PublicModulePage />} />
                    <Route path="/admin/feedback" element={<FeedbackAdmin />} />
                    <Route path="/admin/intent-metrics" element={<IntentMetricsAdminPage />} />
                    <Route path="/admin/loops" element={<AutoFixLoopDashboard />} />
                    <Route
                      path="/my-apps"
                      element={
                        <SentryErrorBoundary name="MyAppsPage">
                          <MyAppsPage />
                        </SentryErrorBoundary>
                      }
                    />
                    {/* 🟥 CUADRO NEGRO 2026-06 · WORKSPACE ÚNICO — Vista dedicada
                        de trabajos: única lista y único lugar donde se elige el
                        trabajo activo. */}
                    <Route
                      path="/mis-trabajos"
                      element={
                        <SentryErrorBoundary name="MisTrabajosPage">
                          <MisTrabajosPage />
                        </SentryErrorBoundary>
                      }
                    />
                    <Route path="/my-works" element={<Navigate to="/mis-trabajos" replace />} />
                    {/* 🟦 CUADRO NEGRO #1 — Vista independiente de proyectos del Builder. */}
                    <Route
                      path="/builder-projects"
                      element={
                        <SentryErrorBoundary name="BuilderProjectsPage">
                          <BuilderProjectsPage />
                        </SentryErrorBoundary>
                      }
                    />
                    {/* 🟥 CUADRO NEGRO MAESTRO — acceso único al Builder visual. */}
                    <Route
                      path="/builder-visual/:sessionId"
                      element={
                        <SentryErrorBoundary name="UiBuilderPage">
                          <UiBuilderPage />
                        </SentryErrorBoundary>
                      }
                    />
                    <Route path="/capabilities" element={<Capabilities />} />

                    <Route path="/feedback" element={<FeedbackPage />} />
                    <Route path="/dashboard/referrals" element={<ReferralsDashboard />} />
                    <Route path="/dashboard/notifications" element={<NotificationsPage />} />
                    <Route path="/my-submissions" element={<UserSubmissionsPage />} />
                    <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                    <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
                    <Route path="/terminos" element={<Terminos />} />
                    <Route path="/privacidad" element={<Privacidad />} />
                    {/* CUADRO NEGRO — Alias en inglés que renderizan el
                        MISMO componente. No duplica contenido legal. */}
                    <Route path="/terms" element={<Terminos />} />
                    <Route path="/privacy" element={<Privacidad />} />
                    <Route path="/precios" element={<Precios />} />
                    <Route path="/historial" element={<HistorialAvanzado />} />
                    <Route path="/dashboard/pipelines" element={<PipelinesPage />} />
                    <Route path="/dashboard/automations" element={<AutomationsPage />} />
                    <Route path="/dashboard/workspace" element={<WorkspacePage />} />
                    <Route path="/dashboard/workspace/editor/:id" element={<WorkspaceTemplateEditorPage />} />
                    <Route path="/dashboard/usage" element={<UserUsage />} />
                    <Route path="/developer" element={<DevPortal />} />
                    <Route path="/public/dashboard/:token" element={<PublicDashboard />} />
                    <Route path="/preview/:token" element={<TemporaryPreviewPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </MainLayout>
                  </Suspense>
                  {/* CUADRO NEGRO — Footer condicional: oculto en Builder,
                      MyApps, BuilderProjects, Workspace, Dashboard, Admin. */}
                  <RouteAwareFooter />
                </BrowserRouter>
                {/* 🟥 CUADRO NEGRO SUPREMO 2026-02-26 — ConsentGate eliminado completamente. */}
                <Toaster position="top-center" richColors closeButton toastOptions={{
                  style: {
                    fontSize: '16px',
                    padding: '14px 18px',
                    minHeight: '60px',
                    minWidth: '360px',
                    borderWidth: '2px',
                  },
                  className: 'damyxia-toast-lg',
                }} />
                <FuncionPremiumModal />
                <SubscriptionUpgradeOverlayMount />
                <IOSInstallBanner />
                <IOSInstallConfirmToast />
                <PwaUpdateBanner />
              </div>
            </OnboardingProvider>
          </WelcomeProvider>
        </PWAProvider>
        </VoiceProvider>
        </WorkspaceProvider>
        </GlobalUIProvider>
      </LanguageProvider>
    );
  }

  return (
    <LanguageProvider>
      <GlobalUIProvider>
      <WorkspaceProvider>
      <VoiceProvider>
      <PWAProvider>
        <WelcomeProvider>
          <OnboardingProvider>
            <NotificationsProvider>
              <UserLimitsProvider>
              <div className="App">
                <SplashScreen />
                <BrowserRouter>
                  <VerificationGate isVerified={isVerified} userEmail={userEmail} />
                  {/* 🟥 P4.3 2026-07-06 — Suspense global para lazy routes. */}
                  <Suspense fallback={null}>
                  <MainLayout>
                  <Routes>
                    <Route 
                      path="/" 
                      element={
                        <Home 
                          token={token} 
                          onLogout={handleLogout} 
                          onRequestLogout={handleRequestLogout}
                          onLoginClick={() => window.location.href = '/login'}
                          isVerified={isVerified}
                          onVerified={handleVerified}
                        />
                      } 
                    />
                    <Route 
                      path="/login" 
                      element={<Navigate to="/" replace />} 
                    />
                    <Route path="/verify" element={<VerifyEmailPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/shared/:shareCode" element={<SharedConversation />} />
                    <Route path="/share/recipe" element={<SharedRecipePage />} />
                    {/* 🟦 SPRINT 3 — Marketplace público (acceso sin auth) */}
                    {/* 🟥 P4.2 — Envuelto en Suspense por lazy-loading del chunk marketplace. */}
                    <Route path="/marketplace" element={<Suspense fallback={null}><PublicMarketplacePage /></Suspense>} />
                    <Route path="/marketplace/remixes" element={<Suspense fallback={null}><RemixesHallOfFamePage /></Suspense>} />
                    <Route path="/marketplace/r/:id" element={<Suspense fallback={null}><PublicRecipePage /></Suspense>} />
                    <Route path="/design-system" element={<DesignSystemShowcase />} />
                    <Route path="/design-system/gallery" element={<CommunityGallery />} />
                    <Route path="/design-system/shared/:shareCode" element={<SharedDesignView />} />
                    
                    <Route path="/app/:slug" element={<AppViewer />} />
                    <Route path="/planes-y-beneficios" element={<PlanesYBeneficios />} />
                    <Route path="/planes" element={<PlanesYBeneficios />} />
                    {/* 🟥 P1 feb-2026 — `/home` también accesible para
                        usuarios autenticados (target del redirect
                        `/builder` → `/home`). Renderiza la página de
                        marketing Home.jsx con el LyonHeaderButton en su
                        top-bar para que el Genio sea accesible desde
                        ahí también. */}
                    <Route path="/home" element={<HomePage />} />
                    {/* 🟥 CUADRO NEGRO HistoriaDamyxia (feb-2026) — pantalla
                        "Historia con DamyxIA" alimentada por Insights v5. */}
                    <Route path="/historia" element={<HistoriaDamyxia />} />
                    <Route path="/historia-damyxia" element={<HistoriaDamyxia />} />
                    <Route path="/dashboard/historia" element={<HistoriaDamyxia />} />
                    <Route path="/builder" element={<Navigate to="/home" replace />} />
                    <Route path="/sistema-de-recompensas" element={<SistemaDeRecompensas />} />
                    <Route path="/admin/referrals" element={<ReferralsAdmin />} />
                    <Route path="/admin/audit-analytics" element={<AuditAnalyticsPage />} />
                    <Route path="/public/audit-report/:token" element={<PublicAuditReportPage />} />
                    <Route path="/public/module/:slug" element={<PublicModulePage />} />
                    <Route path="/dashboard/referrals" element={<ReferralsDashboard />} />
                    <Route path="/dashboard/notifications" element={<NotificationsPage />} />
                    <Route path="/my-submissions" element={<UserSubmissionsPage />} />
                    <Route path="/my-components" element={<MyComponentsPage />} />
                    <Route path="/referidos" element={<ReferidosPage />} />
                    <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                    <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
                    {/* 🟥 CUADRO NEGRO 2026-02-20 — Return URL del Stripe Billing Portal. */}
                    <Route path="/configuracion/suscripcion" element={<SubscriptionReturnPage />} />
                    {/* 🟥 CUADRO NEGRO Panel Conversión (feb-2026) — Return URL post Stripe Checkout. */}
                    <Route path="/suscripcion/completada" element={<SubscripcionCompletadaPage />} />
                    <Route path="/creator-panel" element={<Navigate to="/admin/creator" replace />} />
                    <Route path="/admin/creator" element={<CreatorDashboard />} />
                    <Route path="/admin/builder" element={<BuilderPage />} />
                    <Route path="/admin/backups" element={<BackupsAdminPage />} />
                    {/* 🟦 Mini-Dashboard Observability — sub-ruta dedicada para deep-link */}
                    <Route path="/admin/creator/observability" element={<CreatorDashboard />} />
                    {/* 🟥 CUADRO NEGRO — Hub "Lyon-IA Valoraciones" (sólo marcmarl2@gmail.com) */}
                    <Route path="/admin/lyon-valoraciones" element={<LyonValoracionesPage />} />
                    <Route path="/admin/moderation" element={<CreatorModerationPage />} />
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route path="/admin/logs" element={<AdminLogs />} />
                    <Route path="/admin/settings" element={<AdminSettings />} />
                    <Route path="/admin/images" element={<AdminImages />} />
                    <Route path="/admin/mcsu" element={<AdminMCSU />} />
                    <Route path="/admin/mcsu/view" element={<MCSUWorkspaceView />} />
                    <Route path="/admin/security" element={<AdminSecurityEvents />} />
                    <Route path="/admin/panel" element={<Suspense fallback={null}><AdminPanel /></Suspense>} />
                    <Route path="/admin/email" element={<AdminEmail />} />
                    <Route path="/admin/feedback" element={<FeedbackAdmin />} />
                    <Route path="/admin/intent-metrics" element={<IntentMetricsAdminPage />} />
                    <Route path="/admin/loops" element={<AutoFixLoopDashboard />} />
                    <Route
                      path="/my-apps"
                      element={
                        <SentryErrorBoundary name="MyAppsPage">
                          <MyAppsPage />
                        </SentryErrorBoundary>
                      }
                    />
                    {/* 🟥 CUADRO NEGRO 2026-06 · WORKSPACE ÚNICO — Vista dedicada
                        de trabajos: única lista y único lugar donde se elige el
                        trabajo activo. */}
                    <Route
                      path="/mis-trabajos"
                      element={
                        <SentryErrorBoundary name="MisTrabajosPage">
                          <MisTrabajosPage />
                        </SentryErrorBoundary>
                      }
                    />
                    <Route path="/my-works" element={<Navigate to="/mis-trabajos" replace />} />
                    {/* 🟦 CUADRO NEGRO #1 — Vista independiente de proyectos del Builder. */}
                    <Route
                      path="/builder-projects"
                      element={
                        <SentryErrorBoundary name="BuilderProjectsPage">
                          <BuilderProjectsPage />
                        </SentryErrorBoundary>
                      }
                    />
                    {/* 🟥 CUADRO NEGRO MAESTRO — El Builder visual SOLO se abre
                        con una sesión temporal creada desde /builder-projects. */}
                    <Route
                      path="/builder-visual/:sessionId"
                      element={
                        <SentryErrorBoundary name="UiBuilderPage">
                          <UiBuilderPage />
                        </SentryErrorBoundary>
                      }
                    />
                    <Route path="/feedback" element={<FeedbackPage />} />
                    <Route path="/capabilities" element={<Capabilities />} />

                    <Route path="/terminos" element={<Terminos />} />
                    <Route path="/privacidad" element={<Privacidad />} />
                    {/* CUADRO NEGRO — Alias en inglés que renderizan el
                        MISMO componente. No duplica contenido legal. */}
                    <Route path="/terms" element={<Terminos />} />
                    <Route path="/privacy" element={<Privacidad />} />
                    <Route path="/precios" element={<Precios />} />
                    <Route path="/historial" element={<HistorialAvanzado />} />
                    <Route path="/dashboard/pipelines" element={<PipelinesPage />} />
                    <Route path="/dashboard/automations" element={<AutomationsPage />} />
                    <Route path="/dashboard/workspace" element={<WorkspacePage />} />
                    <Route path="/dashboard/workspace/editor/:id" element={<WorkspaceTemplateEditorPage />} />
                    <Route path="/workspace/internal" element={<OpenInternalWorkspaceRedirect />} />
                    <Route path="/dashboard/usage" element={<UserUsage />} />
                    <Route path="/developer" element={<DevPortal />} />
                    <Route path="/login" element={<LoginRedirect />} />
                    <Route path="/public/dashboard/:token" element={<PublicDashboard />} />
                    <Route path="/preview/:token" element={<TemporaryPreviewPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </MainLayout>
                  </Suspense>
                  {/* CUADRO NEGRO — Footer condicional: oculto en Builder,
                      MyApps, BuilderProjects, Workspace, Dashboard, Admin. */}
                  <RouteAwareFooter />
                  {/* 🟥 LegalGate usa useNavigate → DEBE estar dentro del Router. */}
                  <LegalGate isAuthenticated={!!token} />
                  {/* 🟦 Toast in-app SSE para submission_approved/rejected.
                      Solo se activa con sesión (!!token). useNavigate
                      requiere estar dentro del Router. */}
                  <NotificationToastListener enabled={!!token} />
                </BrowserRouter>
                
                <LogoutConfirmModal
                  isOpen={showLogoutModal}
                  onClose={() => setShowLogoutModal(false)}
                  onSaveAndLogout={handleSaveAndLogout}
                  onLogoutWithoutSave={handleLogoutWithoutSave}
                />
                
                {/* Terms Re-acceptance Modal - Blocks access until accepted */}
                <TermsReacceptModal
                  isOpen={termsUpdateRequired}
                  onAccept={handleTermsAccepted}
                  onLogout={handleLogout}
                  currentVersion={currentTermsVersion}
                  userEmail={userEmail}
                />
                
                {/* 🟥 CUADRO NEGRO SUPREMO 2026-02-26 — ConsentGate eliminado completamente. */}
                <RestoreUndoBanner />
                <OnboardingTour />
                <Toaster position="top-center" richColors closeButton toastOptions={{
                  style: {
                    fontSize: '16px',
                    padding: '14px 18px',
                    minHeight: '60px',
                    minWidth: '360px',
                    borderWidth: '2px',
                  },
                  className: 'damyxia-toast-lg',
                }} />
                <FuncionPremiumModal />
                <SubscriptionUpgradeOverlayMount />
                <IOSInstallBanner />
                <IOSInstallConfirmToast />
                <PwaUpdateBanner />
                {/* 🟥 Auto-Visual Creation (Fase 2 FULL) — modal global. */}
                <AutoVisualCreationModal />
              </div>
              </UserLimitsProvider>
            </NotificationsProvider>
          </OnboardingProvider>
        </WelcomeProvider>
      </PWAProvider>
      </VoiceProvider>
      </WorkspaceProvider>
      </GlobalUIProvider>
    </LanguageProvider>
  );
}

export default App;

// 🟥 P26.40 · Entrada por SUBDOMINIO propio de un sitio publicado
// (mi-proyecto.damyxia.site): se sirve SOLO el sitio, sin la app DamyxIA.
export const AppRoot = () => (
  isSiteSubdomain()
    ? <Suspense fallback={null}><SubdomainSitePage /></Suspense>
    : <App />
);
